import { useEffect } from "react";
import { useSelector } from "react-redux";

import { PLATFORM_BRAND } from "@/config/platformBrand";
import type { RootState } from "@/redux/store";

/**
 * Puts the tenant's name and icon on the browser tab.
 *
 * The last piece of white-labelling that was never wired up: favicon and title were storable and
 * editable, but `index.html` shipped a Vite placeholder and nothing in the app ever changed either.
 * A school opening its own branded domain still saw "Vite + React + TS" and a Vite logo in the tab,
 * which is the most visible possible break in a white-label product.
 *
 * Reads the pre-authentication host resolution first so the tab is correct on the login screen, then
 * the authenticated tenant record once that loads — the two agree, but the first arrives sooner.
 */
export function TenantDocumentChrome() {
  const hostTenant = useSelector((state: RootState) => state.hostTenant.tenant);
  const authenticatedTenant = useSelector(
    (state: RootState) => state.tenantBranding.current,
  );

  const name = authenticatedTenant?.name ?? hostTenant?.tenantName ?? PLATFORM_BRAND.name;
  const faviconUrl = authenticatedTenant?.faviconUrl ?? hostTenant?.faviconUrl ?? null;

  useEffect(() => {
    document.title = name;
  }, [name]);

  useEffect(() => {
    const href = faviconUrl ?? PLATFORM_BRAND.faviconHref;

    // Reuse the existing tag rather than appending: browsers honour the first icon link, so
    // appending a second one leaves the placeholder in place on some of them.
    let link = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');

    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }

    if (link.href !== href) {
      link.href = href;
    }

    // A tenant favicon may be any format the school uploaded; letting the browser sniff it is more
    // forgiving than asserting a type that might be wrong.
    link.removeAttribute("type");
  }, [faviconUrl]);

  return null;
}
